import { Vec3 as PcVec3, type AppBase } from 'playcanvas';
import {
  sampleAnimTrackRoute,
  type CameraLike,
  type ViewerSceneOptions,
} from '@mindcloudxai/shared';
import type { DebugLineApp } from './types';
import { TRACK_ROUTE_COLOR } from './constants';
import {
  buildCameraFrustumSegments,
  type DebugLineSegment,
  type TrackRouteCameraPose,
} from './track-route-overlay';

function asDebugLineApp(app: AppBase): DebugLineApp | null {
  const candidate = app as AppBase & { drawLine?: unknown };
  return typeof candidate.drawLine === 'function' ? (candidate as DebugLineApp) : null;
}

function toRoutePose(pose: CameraLike): TrackRouteCameraPose {
  return {
    position: new PcVec3(pose.position.x, pose.position.y, pose.position.z),
    target: new PcVec3(pose.target.x, pose.target.y, pose.target.z),
    fov: pose.fov,
  };
}

function resolveKeyframeMarkerSize(points: PcVec3[]): number {
  const center = new PcVec3();
  for (const point of points) center.add(point);
  center.mulScalar(1 / points.length);
  const radius = points.reduce((max, point) => Math.max(max, point.distance(center)), 0);
  return Math.max(radius * 0.018, 0.03);
}

/** 创建运镜路径调试可视化，返回 dispose 函数用于清理。 */
export function createTrackRouteDebug(
  app: AppBase,
  options: ViewerSceneOptions,
): (() => void) | null {
  if (!options.debugTrackRoute) return null;
  const debugApp = asDebugLineApp(app);
  if (!options.animTrack) return null;
  const { route, keyframes } = sampleAnimTrackRoute(options.animTrack);
  const routePoses = route.map(toRoutePose);
  const keyframePoses = keyframes.map(toRoutePose);
  const points = routePoses.map((pose) => pose.position);
  if (!debugApp || points.length < 2) return null;

  const markerSize = resolveKeyframeMarkerSize(points);
  const routeSegments: DebugLineSegment[] = [];
  for (let i = 0; i + 1 < points.length; i += 1) {
    routeSegments.push({ start: points[i], end: points[i + 1], color: TRACK_ROUTE_COLOR });
  }
  const markerSegments = keyframePoses.flatMap((pose) =>
    buildCameraFrustumSegments(pose, markerSize),
  );
  // 创建时触发一次渲染，update 回调只画线不强制重绘
  app.renderNextFrame = true;
  const onUpdate = () => {
    for (const segment of routeSegments) {
      debugApp.drawLine(segment.start, segment.end, segment.color, false);
    }
    for (const segment of markerSegments) {
      debugApp.drawLine(segment.start, segment.end, segment.color, false);
    }
  };

  app.on('update', onUpdate);
  return () => {
    app.off('update', onUpdate);
  };
}
